/**
 * Permanent account deletion (App Store 5.1.1(v) / Play requirement). A hard,
 * unambiguous warning + password confirmation, then POST /api/account/delete —
 * which anonymizes the identity, removes society memberships and revokes every
 * session. Distinct from Deactivate (which is reversible): this cannot be undone.
 */
import { router } from 'expo-router';
import React from 'react';
import { View } from 'react-native';

import { space } from '@bero/tokens';
import { InfoNote } from '@/components/gate/bits';
import { goBack } from '@/features/account-admin/bits';
import { Icon } from '@/features/account-admin/icons';
import { BottomBar } from '@/features/community/bits';
import { useT } from '@/i18n';
import { api } from '@/lib/client';
import { useSession } from '@/lib/session';
import { useTheme } from '@/theme';
import { AppText, Button, Field, Hairline, Input, Screen, TopBar } from '@/ui';

export default function DeleteAccountScreen() {
  const { colors } = useTheme();
  const { t } = useT();
  const { signOut } = useSession();
  const [password, setPassword] = React.useState('');
  const [passwordError, setPasswordError] = React.useState<string | undefined>();
  const [saving, setSaving] = React.useState(false);
  const [notice, setNotice] = React.useState<string | null>(null);

  const submit = async () => {
    if (!password) {
      setPasswordError(t('del_password_error'));
      return;
    }
    setPasswordError(undefined);
    setSaving(true);
    setNotice(null);
    try {
      await api.post<unknown>('/api/account/delete', { password });
      await signOut();
      router.replace('/login');
    } catch (err) {
      const status = (err as { status?: number }).status;
      setNotice(status === 401 || status === 403
        ? t('del_wrong_password')
        : (err as { error?: string }).error ?? t('del_generic_error'));
      setSaving(false);
    }
  };

  return (
    <Screen scroll={false}>
      <TopBar title={t('del_title')} onBack={goBack} />
      <Screen padded style={{ paddingTop: space.s6 }}>
        <Icon name="alert-triangle" size={40} color={colors.danger500} />
        <AppText variant="headingMd" style={{ marginTop: space.s4 }}>
          {t('del_heading')}
        </AppText>
        <AppText variant="bodyMd" tone="secondary" style={{ marginTop: space.s2 }}>
          {t('del_body')}
        </AppText>

        <View style={{ marginTop: space.s5, gap: 10 }}>
          {[t('del_point_signin'), t('del_point_flats'), t('del_point_records')].map((line) => (
            <View key={line} style={{ flexDirection: 'row', gap: space.s2 }}>
              <AppText variant="bodyMd" tone="secondary">•</AppText>
              <AppText variant="bodyMd" tone="secondary" style={{ flex: 1 }}>{line}</AppText>
            </View>
          ))}
        </View>

        <View style={{ marginVertical: space.s5 }}><Hairline /></View>

        <Field label={t('del_confirm_pw')} error={passwordError}>
          <Input value={password} onChangeText={setPassword} revealable placeholder="••••••••" hasError={!!passwordError} />
        </Field>
        {notice ? <InfoNote icon="info" tint={colors.danger500}>{notice}</InfoNote> : null}
      </Screen>
      <BottomBar>
        <Button
          title={t('del_confirm_button')}
          kind="destructive"
          size="lg"
          loading={saving}
          onPress={() => { void submit(); }}
        />
      </BottomBar>
    </Screen>
  );
}
